import {Address} from '../models/mfl';

/**
 * DIS-1870: the one definition of "missing account details". userStore uses it
 * to route a sign-in to ACCOUNT_COLLECT_MISSING_DETAILS, and that screen uses it
 * to name the missing fields and show their errors on arrival.
 */
export type MissingDetailField =
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'dateOfBirth'
  | 'billingAddress'
  | 'phoneNumber';

// Each field is nullable because storeUserInfo copies the API values unchecked.
// Not verified against MFL: the DIS-1870 walkthrough fixture with a null
// email_address made the old .trim() check throw during login.
export type MissingDetailsSource = {
  emailAddress?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  dateOfBirth?: string | null;
  billingAddress?: Address | null;
  phoneNumber?: string | null;
};

// The value MFL stores when it has no real date of birth.
export const PLACEHOLDER_DATE_OF_BIRTH = '1900-01-01';

export const MISSING_DETAIL_LABELS: Record<MissingDetailField, string> = {
  email: 'email address',
  firstName: 'first name',
  lastName: 'last name',
  dateOfBirth: 'date of birth',
  billingAddress: 'billing address',
  phoneNumber: 'mobile number',
};

export const isMissingDateOfBirth = (dateOfBirth?: string | null) =>
  !dateOfBirth || dateOfBirth === PLACEHOLDER_DATE_OF_BIRTH;

const isBlank = (value?: string | null) => !value?.trim();

/** Missing fields, in the order the collect-missing-details form shows them. */
export function getMissingDetailFields(user: MissingDetailsSource): MissingDetailField[] {
  const missing: MissingDetailField[] = [];
  if (isBlank(user.emailAddress)) missing.push('email');
  if (isBlank(user.firstName)) missing.push('firstName');
  if (isBlank(user.lastName)) missing.push('lastName');
  if (isMissingDateOfBirth(user.dateOfBirth)) missing.push('dateOfBirth');
  if (!user.billingAddress?.lines?.length) missing.push('billingAddress');
  if (isBlank(user.phoneNumber)) missing.push('phoneNumber');
  return missing;
}

const joinLabels = (labels: string[]) =>
  labels.length <= 1
    ? labels.join('')
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

/** Intro sentence that names each missing field, or null when none is missing. */
export function missingDetailsIntro(fields: MissingDetailField[]): string | null {
  if (fields.length === 0) return null;
  const labels = joinLabels(fields.map(field => MISSING_DETAIL_LABELS[field]));
  const pronoun = fields.length === 1 ? 'it' : 'them';
  return `Your account is missing your ${labels}. Please add ${pronoun} below to continue.`;
}
